/**
 * widok-klienta.js — kto patrzy na Galerię/Studio i jak wtedy nazywają się silniki masek.
 *
 * LF-SAAS-B1 (2026-08-05). Powód powstania: polityka know-how LF-SaaS mówi, że klient
 * dostaje WYNIK, nie PRZEPIS — a do dziś widział „Pixian", „Pixelcut", „SAM 3" i nasze
 * ceny w złotówkach, czyli listę dostawców i informację, co jest tańsze.
 *
 * ⚠️ ZAKRES OCHRONY (świadomy, zapisany żeby nikt nie uznał tego za niedoróbkę):
 * to warstwa WIDOKU. Nazwy dostawców zostają w kodzie strony, w odpowiedziach serwera
 * i w pamięci przeglądarki — kto otworzy narzędzia programisty, przeczyta je. Zgodnie
 * z polityką (memory feedback-saas-know-how-leak-policy) nazwy modeli i koszty to ryzyko
 * ŚREDNIE i tyle ochrony im się należy; twarde odcięcie u źródła zarezerwowaliśmy dla
 * promptów obrazowych, bo tam jest prawdziwa fosa. Nie rozszerzaj tego pliku o hardening
 * backendu — to osobna robota z własną decyzją.
 *
 * 🔴 CZEGO TU NIE ROBIMY: nie zmieniamy wartości wysyłanych na serwer. `id` pozycji jest
 * dokładnie tym, czym był — zmienia się wyłącznie napis. Klient z zapamiętanym wyborem
 * niedostępnym w chmurze dostaje podmieniony NAPIS (patrz `pozycjeDlaWidoku`), nie inną
 * wartość w żądaniu.
 */

import React from 'react';

/** Czy strona pokazuje widok KLIENTA.
 *
 * Jedno źródło prawdy: klasa `lf-client-mode` na <body>, którą ustawia `shared/client-mode.js`
 * (i tylko on). Czytanie pamięci przeglądarki albo trybu z logowania byłoby drugą drogą do tej
 * samej odpowiedzi, a te dwie rozjeżdżają się dokładnie wtedy, kiedy najbardziej boli —
 * przy wymuszonym widoku klienta w chmurze.
 *
 * Zdarzenie `lf-ui-level` jest tu konieczne, nie kosmetyczne: w chmurze klasa pojawia się
 * PO zamontowaniu widoku (czekamy na odpowiedź o logowaniu), a lokalnie operator przełącza
 * widok w locie. Sam odczyt przy montażu pokazałby nazwy dostawców do czasu odświeżenia.
 */
export function useWidokKlienta() {
  const [klient, setKlient] = React.useState(
    () => typeof document !== 'undefined' && document.body.classList.contains('lf-client-mode')
  );
  React.useEffect(() => {
    const naZmiane = (e) => {
      const poziom = (e && e.detail && e.detail.poziom)
        || (document.body.classList.contains('lf-client-mode') ? 'client' : 'full');
      setKlient(poziom === 'client');
    };
    document.addEventListener('lf-ui-level', naZmiane);
    // Domknięcie wyścigu: gdyby client-mode.js zdążył ogłosić poziom, zanim ten widok się
    // podłączył, zdarzenia już nie powtórzy — czytamy stan raz, po zamontowaniu.
    naZmiane(null);
    return () => document.removeEventListener('lf-ui-level', naZmiane);
  }, []);
  return klient;
}

/* ─────────────────────────────────────────────────────────────────────────────
 * O137 (2026-10-20, decyzja operatora 2026-10-19 15:09: Beta „jak nowy użytkownik” na koncie
 * WŁAŚCICIELA, bez przenosin danych z Maca). Którą drogą Studio podmienia zdjęcia na oryginały
 * z telefonu — to NIE jest pytanie o widok. Widok mówi, co pokazać (napisy, dostawcy), a droga
 * zależy od tego, gdzie leżą oryginały: stary Photo Match czyta folder na DYSKU SERWERA (tylko Mac;
 * w chmurze 404 przez `lf_studio_guard`), „Podmień na oryginały” bierze je z przeglądarki.
 * Właściciel w chmurze ma widok pełny, a oryginałów na serwerze nie ma — więc droga przeglądarkowa.
 * ───────────────────────────────────────────────────────────────────────────── */

/** Czy strona działa w CHMURZE (logowanie włączone). Źródło: `window.LF_AUTH.isEnabled()` z
 *  `shared/auth.js` — przed gotowością logowania oddaje `false`, dlatego `obserwujChmure` czyta
 *  ponownie po `LF_AUTH.ready`. Brak obiektu albo wyjątek = „nie chmura”: kierunek bezpieczny, bo
 *  strona bez logowania to Mac, a pasek i tak pojawia się dopiero po wczytaniu zdjęć (po gotowości). */
export function czyChmura(lfAuth) {
  try {
    return !!(lfAuth && typeof lfAuth.isEnabled === 'function' && lfAuth.isEnabled());
  } catch (e) {
    return false;
  }
}

/** Woła `naZmiane(chmura)` od razu, po gotowości logowania i przy każdym `lf-ui-level`.
 *  Oddaje funkcję odpinającą. Bez Reacta — żeby kolejność startu dało się sprawdzić w Node. */
export function obserwujChmure(okno, dokument, naZmiane) {
  let zywy = true;
  const odczytaj = () => { if (zywy) naZmiane(czyChmura(okno && okno.LF_AUTH)); };
  dokument.addEventListener('lf-ui-level', odczytaj);
  const auth = okno && okno.LF_AUTH;
  if (auth && auth.ready && typeof auth.ready.then === 'function') auth.ready.then(odczytaj, odczytaj);
  odczytaj();
  return () => { zywy = false; dokument.removeEventListener('lf-ui-level', odczytaj); };
}

export function useWChmurze() {
  const [chmura, setChmura] = React.useState(
    () => typeof window !== 'undefined' && czyChmura(window.LF_AUTH)
  );
  React.useEffect(() => obserwujChmure(window, document, setChmura), []);
  return chmura;
}

/** 'przegladarka' — „Podmień na oryginały” (klient zawsze; właściciel w chmurze), 'dysk' — stary
 *  Photo Match (operator na Macu, bez zmian). Jedyne miejsce tej decyzji (pasek, handlery, okno). */
export function drogaPhotoMatch(klient, chmura) {
  return (klient || chmura) ? 'przegladarka' : 'dysk';
}

/* ─────────────────────────────────────────────────────────────────────────────
 * Pozycje list silnika. `klient: null` = pozycja NIE istnieje w widoku klienta.
 *
 * Apple Vision i „Auto" znikają klientowi nie dla ozdoby: Apple Vision to binarka
 * macOS (WIEDZA_MASKI §13), więc w chmurze na Linuksie nie ma jej czym uruchomić.
 * Pokazanie ich klientowi obiecywałoby darmową ścieżkę, której u niego nie ma.
 * ───────────────────────────────────────────────────────────────────────────── */

/* Napisy dla klienta trzymamy w stałych, bo te same dwie pozycje pojawiają się na TRZECH
 * listach (paczka, pojedyncze zdjęcie, ratunek w FIX). Wpisane trzy razy z palca rozjechałyby
 * się przy pierwszej poprawce — dokładnie tak, jak rozjeżdżały się listy silników w kodzie. */
const KLIENT_STANDARD = 'Standardowe wycinanie — sprawdza się na większości ubrań';
const KLIENT_LEPSZE = 'Lepsze wycinanie — dla trudnych zdjęć, droższe';

/** Lista „Silnik główny (Usuń tło)" — panel maskowania paczki i pojedynczego zdjęcia. */
export const SILNIK_GLOWNY = [
  // LF-AUTO-PIXIAN (2026-07-29): „Auto" przestaje być zamiennikiem awaryjnym, a staje się
  // jedynym wyborem, który DOPŁACA za zbliżenia metek. Etykieta mówi to wprost, bo do dziś
  // dopłacał każdy silnik lokalny — także ten opisany „za darmo" — i operator o tym nie wiedział.
  // 🔴 LF-BIREFNET-USUN (2026-08-04, decyzja operatora): pozycja „BiRefNet" USUNIĘTA — silnik
  // obciążał komputer i nie wracamy. Zapisany wybór `birefnet` jest odsiewany przy odczycie.
  {
    id: 'auto',
    operator: 'Auto — Apple Vision + Pixian tam, gdzie zobaczy zbliżenie metki (dopłata ~1 gr za takie zdjęcie)',
    klient: null,
  },
  {
    id: 'applevision',
    operator: 'Apple Vision (szybki ~0.3s, ZA DARMO — zbliżeń metek nie ratuje, NIE tnie otworów)',
    klient: null,
  },
  {
    id: 'pixian',
    operator: 'Pixian.ai (online, bardzo dobra jakość, płatne ~0,01 zł/zdj.)',
    klient: KLIENT_STANDARD,
  },
  // LF-PIXELCUT-SILNIK (2026-08-05): jedyny silnik, który ratuje zbliżenia metek (3 z 4 pudeł
  // Apple Vision, zmierzone n=50). Cena mówi wprost, że jest 6× droższy od Pixiana — etykieta
  // ma powstrzymać przed ustawieniem go „na wszystko": przy 9 zdjęciach na ogłoszenie
  // kosztowałby tyle, co całe ogłoszenie. U klienta ten sam hamulec niesie słowo „droższe".
  {
    id: 'pixelcut',
    operator: 'Pixelcut (online, NAJLEPSZA jakość — ratuje zbliżenia metek, płatne ~0,06 zł/zdj.)',
    klient: KLIENT_LEPSZE,
  },
];

/** Ta sama lista, ale w panelu POJEDYNCZEGO ZDJĘCIA — operator ma tam krótsze napisy.
 *
 * LF-FIX-TAB (2026-07-29): ta sama KOLEJNOŚĆ co w panelu paczki, wg obciążenia komputera —
 * rozjazd między dwiema listami tego samego wyboru byłby pułapką (dlatego BiRefNet wyszedł
 * z obu naraz, LF-BIREFNET-USUN 2026-08-04). Napisy dla klienta biorą się ze wspólnych
 * stałych, więc rozjechać się mogą już tylko te dla operatora — i to widać w jednym pliku.
 */
export const SILNIK_GLOWNY_ZDJECIE = [
  { id: 'auto', operator: 'Auto — Apple Vision + Pixian na zbliżenia metek (dopłata ~1 gr)', klient: null },
  { id: 'applevision', operator: 'Apple Vision (szybki ~0.3s, ZA DARMO — zbliżeń metek nie ratuje)', klient: null },
  { id: 'pixian', operator: 'Pixian.ai (online, bardzo dobra jakość, płatne ~0,01 zł/zdj.)', klient: KLIENT_STANDARD },
  // ⚠️ W PACZCE cena mnoży się przez liczbę zdjęć: 9 zdjęć ogłoszenia = ~0,54 zł, tyle co całe
  // ogłoszenie. U klienta ten sam hamulec niesie słowo „droższe".
  { id: 'pixelcut', operator: 'Pixelcut (online, NAJLEPSZA jakość — ratuje zbliżenia metek, płatne ~0,06 zł/zdj.)', klient: KLIENT_LEPSZE },
];

/** Lista ratunkowa w zakładce FIX (objaw „wycięło zupełnie nie to"). */
export const SILNIK_RATUNKOWY = [
  // LF-ZAAWANSOWANE-AUDYT (2026-07-31): lista wyrównana do maszynowni — zmierzone są dwie
  // drogi, reszta to warianty tego samego wyboru. Ta pozycja jest DOMYŚLNA i darmowa
  // (auto-tło na SAM 2), więc zostaje w obu widokach i bez zmiany napisu: wartość domyślna
  // nie może po cichu włączać płatnego licznika.
  {
    id: 'inny-niz-maska',
    operator: 'automatycznie (zalecane)',
    klient: 'automatycznie (zalecane)',
  },
  {
    id: 'pixian',
    operator: 'Pixian — gdy wycięło zupełnie nie to (płatne ~0,01 zł/zdj.)',
    klient: 'Standardowe wycinanie — policz maskę od nowa',
  },
  // LF-PIXELCUT-SILNIK (2026-08-05): ostatnia deska ratunku — najwyższa zmierzona jakość,
  // ale 6× cena Pixiana. Świadomie NIŻEJ na liście i świadomie NIE domyślna.
  {
    id: 'pixelcut',
    operator: 'Pixelcut — gdy nawet Pixian nie dał rady (płatne ~0,06 zł/zdj.)',
    klient: 'Lepsze wycinanie — gdy standardowe nie dało rady, droższe',
  },
];

/** Lista „czym dokładać zjedzone fragmenty" w zakładce Maska pojedynczego zdjęcia.
 *
 * LF-ZAAWANSOWANE-AUDYT (2026-07-31): skrócona z 6 pozycji do 2 — zmierzona jest jedna droga
 * („inny niż ten od maski", odzysk 98,2% mediany, N=6) i ratunkowy Pixian (pudła na zbliżeniach
 * metek 14% → 4%, N=50). 🔴 LF-BIREFNET-USUN (2026-08-04): „automatycznie" znaczy odtąd
 * Auto-tło (SAM 2), nie BiRefNet — i pozostaje DARMOWE, bo wartość domyślna nie może po cichu
 * włączać płatnego licznika.
 * ⚠️ To osobna lista niż ratunkowa z zakładki FIX — świadomie krótsza. Nie scalaj ich
 * „dla porządku": dołożenie tu Pixelcuta byłoby zmianą oferty, nie zmianą napisu.
 */
export const SILNIK_DOKLADANIE = [
  {
    id: 'inny-niz-maska',
    operator: '↳ czym dokładać: automatycznie (zalecane)',
    klient: '↳ czym dokładać: automatycznie (zalecane)',
  },
  {
    id: 'pixian',
    operator: '↳ czym dokładać: Pixian — gdy wycięło zupełnie nie to (płatne ~0,01 zł/zdj.)',
    klient: '↳ czym dokładać: standardowym wycinaniem — gdy wycięło zupełnie nie to',
  },
];

/** Jak nazwać silnik, którym maska POWSTAŁA (komunikaty po operacji, podpowiedzi w Galerii). */
const NAZWA_UZYTEGO = {
  auto:            { operator: 'Auto',                 klient: 'wycinanie standardowe' },
  applevision:     { operator: 'Apple Vision',         klient: 'wycinanie standardowe' },
  local:           { operator: 'Apple Vision',         klient: 'wycinanie standardowe' },
  pixian:          { operator: 'Pixian',               klient: 'wycinanie standardowe' },
  pixelcut:        { operator: 'Pixelcut',             klient: 'wycinanie lepsze' },
  'sam-bg-auto':   { operator: 'Auto-tło',             klient: 'dopasowanie automatyczne' },
  'sam3-wieszak':  { operator: 'Doklejanie wieszaka',  klient: 'doklejenie wieszaka' },
  photoroom:       { operator: 'Photoroom',            klient: 'Twoje własne wycięcie' },
};

/** Zapasowa nazwa, gdy identyfikator jest nieznany. Dla operatora pokazujemy surowy
 *  identyfikator (diagnostyka), dla klienta NIGDY — surowa nazwa nowego silnika wyciekłaby
 *  przez samo to, że ktoś zapomniał dopisać go wyżej. */
export function nazwaZapasowa(id, klient) {
  return klient ? 'wycinanie' : id;
}

/* ─────────────────────────────────────────────────────────────────────────────
 * Galeria: filtry po źródle maski i podpowiedź przy kropce na kafelku.
 * Klucze filtrów (`pixian`, `pixelcut`, …) zostają bez zmian — filtrowanie działa
 * dokładnie jak dotąd, zmienia się wyłącznie napis na chipie.
 * ───────────────────────────────────────────────────────────────────────────── */

const ETYKIETY_FILTRA = {
  raw:          { operator: 'Surowe',           klient: 'Bez wycięcia' },
  // U operatora ten chip łączy maski lokalne i płatne; u klienta ścieżki lokalnej nie ma,
  // więc zostaje prosty fakt: tło już wycięte.
  local_pixian: { operator: 'Lokalne + płatne', klient: 'Wycięte' },
  pixian:       { operator: 'Pixian',           klient: 'Wycinanie standardowe' },
  pixelcut:     { operator: 'Pixelcut',         klient: 'Wycinanie lepsze' },
  photoroom:    { operator: 'Po Photoroom',     klient: 'Własne wycięcie' },
};

/** Napis na chipie filtra Galerii. Nieznany klucz → sam klucz (dla operatora to sygnał,
 *  że doszedł filtr bez etykiety; u klienta nieznanego klucza nie pokazujemy wcale). */
export function etykietaFiltra(tag, klient) {
  const wpis = ETYKIETY_FILTRA[tag];
  if (!wpis) return klient ? '' : tag;
  return klient ? wpis.klient : wpis.operator;
}

/** Podpowiedź przy kropce źródła maski na kafelku. U operatora z ceną (po to jest kropka:
 *  przelecieć wzrokiem po siatce i zobaczyć, ile poszło najdroższą drogą), u klienta bez. */
export function podpowiedzZrodlaMaski(mask_source, klient) {
  if (klient) {
    if (mask_source === 'pixian') return 'Tło wycięte standardowo';
    if (mask_source === 'pixelcut') return 'Tło wycięte lepszym sposobem';
    if (mask_source === 'photoroom') return 'Twoje własne wycięcie';
    return 'Tło wycięte';
  }
  if (mask_source === 'pixian') return 'Maska: Pixian (online, płatne ~0,01 zł)';
  if (mask_source === 'pixelcut') return 'Maska: Pixelcut (online, najlepsza jakość, płatne ~0,06 zł)';
  return 'Maska: Photoroom (subskrypcja, najlepsza jakość)';
}

/** Pozycje listy dla danego widoku — filtr + podmiana napisu. Wartość `id` bez zmian. */
export function pozycjeDlaWidoku(lista, klient) {
  return lista
    .filter((p) => !klient || p.klient)
    .map((p) => ({ id: p.id, tekst: klient ? p.klient : p.operator }));
}

/** Nazwa silnika w komunikacie. Nieznany identyfikator → null (wołający pomija zdanie,
 *  zamiast pokazywać surowy identyfikator — „nie wiem" nie może wyglądać jak nazwa). */
export function nazwaSilnika(id, klient) {
  const wpis = NAZWA_UZYTEGO[id];
  if (!wpis) return null;
  return klient ? wpis.klient : wpis.operator;
}

/** Czy zapamiętany wybór da się pokazać w tym widoku. Używane tam, gdzie lista klienta jest
 *  krótsza niż zapis w przeglądarce — pokazujemy wtedy pierwszą dostępną pozycję, ale
 *  NIE zmieniamy tego, co poleci na serwer (o tym decyduje kod wołający operację). */
export function widocznyWWidoku(lista, id, klient) {
  const wpis = lista.find((p) => p.id === id);
  return !!wpis && (!klient || !!wpis.klient);
}
